import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import Header from "@/components/Header";
import { api } from "@/lib/api";
import { Trophy, Crown, CheckCircle2, XCircle, RefreshCw, ChevronLeft, BookOpen, Target, Swords } from "lucide-react";

const LEVEL_LABEL = {
  l0: "Round 1", l1: "Semi Final", l2: "Final", l3: "3rd Place",
  L0: "Round 1",  L1: "Semi Final",  L2: "Final",  L3: "3rd Place",
};

function Avatar({ avatar, name, size = 80 }) {
  const initials = (name || "?").split(" ").map((w) => w[0]).join("").slice(0, 2).toUpperCase();
  return (
    <div
      className="rounded-2xl overflow-hidden flex-shrink-0"
      style={{ width: size, height: size * 1.3, background: "rgba(255,255,255,0.05)", border: "1.5px solid rgba(255,255,255,0.10)" }}
    >
      {avatar?.img ? (
        <img src={avatar.img} alt={name} className="w-full h-full object-cover object-top"
          onError={(e) => { e.currentTarget.style.display = "none"; }} />
      ) : (
        <div className="w-full h-full flex items-center justify-center font-display text-3xl text-white/20">
          {initials}
        </div>
      )}
    </div>
  );
}

function StatPill({ label, value, color }) {
  return (
    <div className="flex flex-col items-center gap-1 px-4 py-3 rounded-xl"
      style={{ background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.08)" }}>
      <span className="font-mono-kl text-2xl md:text-3xl font-bold" style={{ color }}>{value}</span>
      <span className="font-mono-kl text-[9px] uppercase tracking-widest text-white/40">{label}</span>
    </div>
  );
}

function PlayerSummaryCard({ player, isWinner, side }) {
  const accuracy = player.correct + player.wrong > 0
    ? Math.round((player.correct / (player.correct + player.wrong)) * 100)
    : 0;
  const initials = (player?.name || "?").split(" ").map((w) => w[0]).join("").slice(0, 2).toUpperCase();
  const flip = side === "right";

  return (
    <div
      className="rounded-2xl overflow-hidden flex flex-col relative"
      style={{
        background: isWinner ? "rgba(230,57,70,0.07)" : "rgba(255,255,255,0.03)",
        border: isWinner ? "1.5px solid rgba(230,57,70,0.45)" : "1.5px solid rgba(255,255,255,0.09)",
        boxShadow: isWinner ? "0 0 40px rgba(230,57,70,0.18)" : "none",
      }}
    >
      {/* Winner crown badge */}
      {isWinner && (
        <div className="absolute top-4 right-4 z-10 flex items-center gap-1.5 px-3 py-1 rounded-full"
          style={{ background: "var(--kl-red)", boxShadow: "0 0 14px rgba(230,57,70,0.6)" }}>
          <Crown className="w-3.5 h-3.5 text-white" />
          <span className="font-mono-kl text-[9px] uppercase tracking-widest text-white">Winner</span>
        </div>
      )}

      {/* Avatar — full width, portrait */}
      <div className="w-full relative overflow-hidden" style={{ height: 260 }}>
        {player.avatar?.img ? (
          <img
            src={player.avatar.img}
            alt={player.name}
            className="w-full h-full object-cover object-top"
            onError={(e) => { e.currentTarget.style.display = "none"; }}
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center font-display text-7xl"
            style={{
              background: isWinner ? "rgba(230,57,70,0.12)" : "rgba(255,255,255,0.04)",
              color: isWinner ? "rgba(230,57,70,0.5)" : "rgba(255,255,255,0.12)",
            }}>
            {initials}
          </div>
        )}
        {/* Gradient overlay at bottom */}
        <div className="absolute inset-x-0 bottom-0 h-24 pointer-events-none"
          style={{ background: "linear-gradient(to top, rgba(6,10,31,0.95), transparent)" }} />
      </div>

      {/* Name + topic */}
      <div className={`px-5 pt-3 pb-2 flex flex-col gap-1 ${flip ? "items-end text-right" : ""}`}>
        <div className="font-display text-3xl md:text-4xl uppercase leading-tight"
          style={{ color: isWinner ? "#e63946" : "#ffffff" }}>
          {player.name}
        </div>
        {player.topic_label && (
          <div className="flex items-center gap-1.5" style={{ justifyContent: flip ? "flex-end" : "flex-start" }}>
            <BookOpen className="w-3 h-3 text-white/35" />
            <span className="font-mono-kl text-[10px] uppercase tracking-widest text-white/45">
              {player.topic_label}
            </span>
          </div>
        )}
      </div>

      {/* Stats row */}
      <div className="grid grid-cols-4 gap-2 px-5 pb-5 pt-2">
        <StatPill label="Score"    value={player.total}   color={isWinner ? "var(--kl-red)" : "#fff"} />
        <StatPill label="Correct"  value={player.correct} color="#4ade80" />
        <StatPill label="Wrong"    value={player.wrong}   color="#f87171" />
        <StatPill label="Accuracy" value={`${accuracy}%`} color={isWinner ? "var(--kl-red)" : "rgba(255,255,255,0.6)"} />
      </div>
    </div>
  );
}

function RoundRow({ round, idx }) {
  const p1Correct = round.p1_correct;
  const p2Correct = round.p2_correct;

  return (
    <div
      className="rounded-xl overflow-hidden"
      style={{ border: "1px solid rgba(255,255,255,0.07)", background: "rgba(255,255,255,0.02)" }}
    >
      {/* Round header */}
      <div className="flex items-center justify-between px-4 py-2.5"
        style={{ background: "rgba(255,255,255,0.04)", borderBottom: "1px solid rgba(255,255,255,0.07)" }}>
        <span className="font-mono-kl text-[10px] uppercase tracking-widest text-white/50">
          Question {round.round_num}
        </span>
        <div className="flex items-center gap-4">
          <span className="font-mono-kl text-xs font-bold" style={{ color: p1Correct ? "#4ade80" : "#f87171" }}>
            P1 {round.p1_option || "—"} {p1Correct ? "✓" : "✗"}
          </span>
          <span className="text-white/20">|</span>
          <span className="font-mono-kl text-xs font-bold" style={{ color: p2Correct ? "#4ade80" : "#f87171" }}>
            P2 {round.p2_option || "—"} {p2Correct ? "✓" : "✗"}
          </span>
        </div>
      </div>

      {/* Questions side by side */}
      <div className="grid md:grid-cols-2 divide-y md:divide-y-0 md:divide-x divide-white/6">
        {/* P1 */}
        <div className="p-4 flex flex-col gap-2">
          <div className="font-mono-kl text-[9px] uppercase tracking-widest text-white/30 mb-1">Player 1</div>
          <p className="text-sm text-white/80 leading-relaxed">{round.p1_question || "—"}</p>
          <div className="flex items-center gap-2 mt-1">
            {p1Correct
              ? <CheckCircle2 className="w-4 h-4 text-green-400 flex-shrink-0" />
              : <XCircle className="w-4 h-4 text-red-400 flex-shrink-0" />}
            <span className="font-mono-kl text-[10px] uppercase tracking-widest"
              style={{ color: p1Correct ? "#4ade80" : "#f87171" }}>
              Answered: {round.p1_option || "—"} · Correct: {round.p1_correct_ans || "—"}
            </span>
          </div>
          {round.p1_feedback && (
            <p className="text-[11px] text-white/40 italic mt-1 leading-relaxed">{round.p1_feedback}</p>
          )}
        </div>

        {/* P2 */}
        <div className="p-4 flex flex-col gap-2">
          <div className="font-mono-kl text-[9px] uppercase tracking-widest text-white/30 mb-1">Player 2</div>
          <p className="text-sm text-white/80 leading-relaxed">{round.p2_question || "—"}</p>
          <div className="flex items-center gap-2 mt-1">
            {p2Correct
              ? <CheckCircle2 className="w-4 h-4 text-green-400 flex-shrink-0" />
              : <XCircle className="w-4 h-4 text-red-400 flex-shrink-0" />}
            <span className="font-mono-kl text-[10px] uppercase tracking-widest"
              style={{ color: p2Correct ? "#4ade80" : "#f87171" }}>
              Answered: {round.p2_option || "—"} · Correct: {round.p2_correct_ans || "—"}
            </span>
          </div>
          {round.p2_feedback && (
            <p className="text-[11px] text-white/40 italic mt-1 leading-relaxed">{round.p2_feedback}</p>
          )}
        </div>
      </div>
    </div>
  );
}

export default function MatchDetail() {
  const { roomId } = useParams();
  const navigate   = useNavigate();
  const [match, setMatch]   = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError]   = useState(null);

  useEffect(() => {
    api.publicMatchDetail(roomId)
      .then(setMatch)
      .catch((e) => setError(e?.response?.data?.detail || "Match not found"))
      .finally(() => setLoading(false));
  }, [roomId]);

  return (
    <div className="min-h-screen text-white relative overflow-x-hidden" style={{ background: "var(--kl-navy-deep)" }}>
      <Header />

      {/* Bg glow */}
      <div
        className="absolute top-32 left-1/2 -translate-x-1/2 w-[700px] h-[700px] rounded-full opacity-10 blur-3xl pointer-events-none"
        style={{ background: "radial-gradient(circle, rgba(230,57,70,0.55), transparent 65%)" }}
      />

      <div className="relative z-10 w-full px-6 md:px-12 py-10">

        {/* Back */}
        <button
          onClick={() => navigate("/leaderboard")}
          className="font-mono-kl text-xs uppercase tracking-widest text-white/50 hover:text-white flex items-center gap-2 mb-8"
        >
          <ChevronLeft className="w-4 h-4" /> Back to Leaderboard
        </button>

        {loading && (
          <div className="flex items-center gap-3 text-white/40 py-20">
            <RefreshCw className="w-5 h-5 animate-spin" />
            <span className="font-mono-kl text-xs uppercase tracking-widest">Loading match…</span>
          </div>
        )}

        {error && (
          <div className="rounded-2xl border border-red-500/30 p-10 text-center" style={{ background: "rgba(239,68,68,0.05)" }}>
            <div className="font-display text-3xl uppercase text-red-400">{error}</div>
          </div>
        )}

        {match && (
          <>
            {/* ── Match hero ── */}
            <div className="font-mono-kl text-xs uppercase tracking-[0.35em] text-[color:var(--kl-red)] flex items-center gap-2 mb-2">
              <Swords className="w-3.5 h-3.5" />
              {LEVEL_LABEL[match.level] || "Match"} · Match #{match.match_num ?? "—"}
            </div>
        <h1 className="font-display text-4xl md:text-7xl uppercase leading-[0.88] tracking-tight mb-1">
          {match.player1?.name} <span className="text-[color:var(--kl-red)]">vs</span> {match.player2?.name}
        </h1>
            <div className="font-mono-kl text-xs uppercase tracking-widest text-white/35 mb-10">
              {match.tournament_name} · {match.total_rounds} question{match.total_rounds !== 1 ? "s" : ""} played
            </div>

            {/* ── Player summary cards ── */}
            <div className="grid md:grid-cols-2 gap-5 mb-12">
              <PlayerSummaryCard player={match.player1} isWinner={match.winner === 1} side="left" />
              <PlayerSummaryCard player={match.player2} isWinner={match.winner === 2} side="right" />
            </div>

            {/* ── Topic comparison ── */}
            {(match.player1?.topic_label || match.player2?.topic_label) && (
              <div className="mb-10 rounded-2xl p-5 grid md:grid-cols-2 gap-4"
                style={{ background: "rgba(255,255,255,0.02)", border: "1px solid rgba(255,255,255,0.07)" }}>
                <div>
                  <div className="font-mono-kl text-[9px] uppercase tracking-widest text-white/35 mb-1">
                    {match.player1?.name} — Topic
                  </div>
                  <div className="flex items-center gap-2">
                    <Target className="w-4 h-4 text-[color:var(--kl-red)]" />
                    <span className="font-display text-xl uppercase text-white">
                      {match.player1?.topic_label || "—"}
                    </span>
                  </div>
                </div>
                <div>
                  <div className="font-mono-kl text-[9px] uppercase tracking-widest text-white/35 mb-1">
                    {match.player2?.name} — Topic
                  </div>
                  <div className="flex items-center gap-2">
                    <Target className="w-4 h-4 text-[color:var(--kl-red)]" />
                    <span className="font-display text-xl uppercase text-white">
                      {match.player2?.topic_label || "—"}
                    </span>
                  </div>
                </div>
              </div>
            )}

            {/* ── Round-by-round breakdown ── */}
            {match.rounds?.length > 0 && (
              <section>
                <div className="font-mono-kl text-[11px] uppercase tracking-[0.3em] text-white/40 flex items-center gap-2 mb-5">
                  <BookOpen className="w-3.5 h-3.5" />
                  Question-by-question breakdown
                </div>
                <div className="flex flex-col gap-4">
                  {match.rounds.map((rd, i) => (
                    <RoundRow key={i} round={rd} idx={i} />
                  ))}
                </div>
              </section>
            )}

            {match.rounds?.length === 0 && (
              <div className="rounded-2xl border border-white/8 p-10 text-center" style={{ background: "rgba(255,255,255,0.02)" }}>
                <div className="font-display text-2xl uppercase text-white/30">No round data available</div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
